import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { Notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SuperTokensWrapper } from 'supertokens-auth-react';

import 'dayjs/locale/fr';
import 'dayjs/locale/en';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-datatable/styles.layer.css';
import '@mantine/dropzone/styles.css';
import '@mantine/carousel/styles.css';
import { ErrorProvider } from 'src/Providers/ErrorProvider';
import { ModalsProvider } from 'src/Providers/ModalsProvider';
import { DayjsLocalizationProvider } from 'src/Services/DayjsLocalizationProvider/component';
import { lightTheme } from 'src/Services/Themes/lightTheme';
import { UserConsentProvider } from 'src/Services/UserConsentProvider/component';

import { GlobalLoaderProvider } from './GlobalLoaderProvider';

const Providers = ({ children }: { children: React.ReactElement }) => {
  // The QueryClient must be stable across re-renders: re-instantiating it wipes the
  // whole react-query cache and refires every mounted query simultaneously.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              if (typeof error === 'object' && error !== null) {
                if ('message' in error && error.message === '403') {
                  return false;
                }

                if (
                  'response' in error &&
                  error.response &&
                  typeof error.response === 'object' &&
                  'status' in error.response &&
                  error.response.status === 403
                ) {
                  return false;
                }
              }

              return failureCount < 3;
            },
          },
        },
      })
  );

  const { i18n } = useTranslation();

  return (
    <DayjsLocalizationProvider>
      <UserConsentProvider>
        <SuperTokensWrapper>
          <QueryClientProvider client={queryClient}>
            <MantineProvider theme={lightTheme}>
              <ErrorProvider>
                <GlobalLoaderProvider>
                  <ModalsProvider>
                    <Notifications />
                    <DatesProvider settings={{ locale: i18n.language }}>{children}</DatesProvider>
                  </ModalsProvider>
                </GlobalLoaderProvider>
              </ErrorProvider>
            </MantineProvider>
          </QueryClientProvider>
        </SuperTokensWrapper>
      </UserConsentProvider>
    </DayjsLocalizationProvider>
  );
};
export default Providers;
